/**
 * @file This allows us to search images/audio/videos/gif/web/stock
 *  based on user input
 * */
import { fetchJson } from '@padlet/fetch'
import ping from 'bits/ping'
import giphyAttributionImage from 'images/giphy_attribution.png'
import giphyAttributionBlackImage from 'images/giphy_attribution_black.png'
import giphyAttributionWhiteImage from 'images/giphy_attribution_white.png'
import spotifyAttributionImage from 'images/spotify_attribution.png'
import tenorAttributionImage from 'images/tenor_attribution.png'
import unsplashAttributionImage from 'images/unsplash_attribution.png'
import youtubeAttributionImage from 'images/youtube_attribution.png'

const searchTypeMap = {
  web: {
    path: 'web',
  },
  audio: {
    path: 'audio',
  },
  videos: {
    path: 'video',
  },
  images: {
    path: 'images',
  },
  gif: {
    path: 'gif',
  },
  stock: {
    path: 'stock',
  },
}

enum SearchFilters {
  None = 'none',
  Web = 'web',
  Audio = 'audio',
  Videos = 'videos',
  Images = 'images',
  Gif = 'gif',
  Stock = 'stock',
}

// @see web_search.rb
interface WebSearchResult {
  url: string
  description: string
  title: string
}

// @see web_search.rb
interface ImageSearchResult {
  height: number
  thumbnail_height: number
  thumbnail_url: string
  thumbnail_width: number
  title: string
  url: string
  width: number
  attribution_link: string
  provider_name?: string
}

// @see image_search.rb web_search.rb unsplash_search.rb
interface VideoSearchResult {
  description: string
  duration?: string
  preview_url?: string
  provider_name: string
  thumbnail_url: string
  thumbnail_width: number
  thumbnail_height: number
  title: string
  url: string
  beacon_link?: string //for Unsplash, @ see unsplash_search.rb
  view_count: number
}

// @see audio_search.rb
interface AudioSearchResult {
  album: string
  artist: string
  description: string
  duration: string
  preview: string
  thumbnail: string
  title: string
  show_name?: string
  url: string
  type: string
  release_date?: Date
  preview_url?: string
  thumbnail_url?: string
}

interface SearchResultDataCollectionMap {
  webpages?: WebSearchResult[]
  audio?: AudioSearchResult[]
  videos?: VideoSearchResult[]
  images?: ImageSearchResult[]
}

interface SearchResult {
  data: SearchResultDataCollectionMap
  links: { self: string; next?: string }
  meta: { provider?: string; total?: number }
}

interface SearchOptions {
  host: string
}

const searchByUrl = async (url: string): Promise<SearchResult> => {
  return fetchJson<SearchResult>(url)
}

const search = async (query: string, filter: SearchFilters, options: SearchOptions | null): Promise<SearchResult> => {
  const searchParams = searchTypeMap[filter]
  const queryParams = { q: query }
  const host = options && options.host ? options.host : ''
  const searchResult = await fetchJson<SearchResult>(`${host}/beethoven/search/${searchParams.path}`, {
    query: queryParams,
  })
  ping(`websearch-${filter}`)
  return searchResult
}

const attributionMap = {
  giphy: {
    height: 11,
    href: 'https://giphy.com',
    src: giphyAttributionImage,
    width: 100,
  },
  giphyBlack: {
    height: 16,
    href: 'https://giphy.com',
    src: giphyAttributionBlackImage,
    width: 45,
  },
  giphyWhite: {
    height: 16,
    href: 'https://giphy.com',
    src: giphyAttributionWhiteImage,
    width: 45,
  },
  spotify: {
    height: 18,
    href: 'https://spotify.com',
    src: spotifyAttributionImage,
    width: 60,
  },
  tenor: {
    height: 10,
    href: 'https://tenor.com',
    src: tenorAttributionImage,
    width: 70,
  },
  unsplash: {
    height: 13,
    href: 'https://unsplash.com',
    src: unsplashAttributionImage,
    width: 57,
  },
  youtube: {
    height: 13,
    href: 'https://youtube.com',
    src: youtubeAttributionImage,
    width: 58,
  },
}

export {
  search,
  searchByUrl,
  SearchFilters,
  SearchResult,
  SearchResultDataCollectionMap,
  attributionMap,
  VideoSearchResult,
  AudioSearchResult,
  ImageSearchResult,
  WebSearchResult,
}
